import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { queryParams } from "src/helpers/UrlHelpers";
import { fetchAvailablePhoneNumbers, fetchAvailablePhoneNumberCountries } from "src/actions/callrecorder/availablePhoneNumbersActions";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import PageLayout from '../_partials/PageLayout';
import PageHeader from "src/componets/PageHeader";
import Wrapper from "src/componets/Wrapper";
import ListTable from "./_partials/_ListTable";
import SidebarIndex from "./_partials/_SidebarIndex";

import { Card, Empty, Select, Spin } from "antd";

const NAV_ITEMS: {[index: number]: any} = {
  1: { id: 1, name: 'No Address',       url: callrecorderUrls.availablePhoneNumbersPath({ address_required: 'false' }) },
  2: { id: 2, name: 'Address Required', url: callrecorderUrls.availablePhoneNumbersPath({ address_required: 'true' }) },
};

const AvailablePhoneNumbers = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = useAppSelector(state => state.callrecorder.availablePhoneNumbers.status);

  const activeNavId = useMemo(() => {
    const addressRequired = searchParams.get('address_required');
    if (addressRequired === 'false') return 1;
    if (addressRequired === 'true') return 2;
    return undefined;
  }, [searchParams]);

  const countryCode = queryParams.get(searchParams, 'country_code');
  const phoneNumberType = queryParams.get(searchParams, 'phone_number_type') || null;

  const phoneNumberTypeOptions = [
    { value: 'local', label: 'Local' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'national', label: 'National' },
    { value: 'tollfree', label: 'Toll Free' }
  ];

  function onPhoneNumberTypeChange(value: string | null) {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('phone_number_type', value);
    } else {
      newParams.delete('phone_number_type');
    }
    setSearchParams(newParams, { replace: true });
  }

  // Fetch countries on mount
  useEffect(() => {
    const params: any = queryParams.requestParams(
      searchParams,
      {},
      ['sms_enabled', 'mms_enabled', 'trunking_enabled', 'address_required']
    );
    dispatch(fetchAvailablePhoneNumberCountries(params));
  }, [searchParams, dispatch]);

  // Fetch numbers when searchParams change
  useEffect(() => {
    if (!countryCode) return;

    const params: any = queryParams.requestParams(
      searchParams,
      { country_code: countryCode },
      ['phone_number_type', 'address_required']
    );

    dispatch(fetchAvailablePhoneNumbers(params));
  }, [searchParams, countryCode, dispatch]);

  return (
    <PageLayout className='v-available-phone-numbers'>
      <PageHeader
        title='Available Phone Numbers'
        navs={NAV_ITEMS}
        activeId={activeNavId}
        parent={{
          name: 'All',
          url: callrecorderUrls.availablePhoneNumbersPath()
        }}
      />

      <div className='v-container'>
        <Wrapper type='type-1' border className='v-mb-10'>
          <Select
            allowClear
            placeholder='Number type'
            value={phoneNumberType}
            onChange={onPhoneNumberTypeChange}
            options={phoneNumberTypeOptions}
            style={{ width: 160 }}
          />
        </Wrapper>

        {status === 'loading' && <Spin />}
        {countryCode && status !== 'loading' && <ListTable />}
        {!countryCode && (
          <Card className='v-mb-20'>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='Select a country to search for available phone numbers'
            />
          </Card>
        )}
      </div>

      <SidebarIndex />
    </PageLayout>
  );
}

export default AvailablePhoneNumbers;
